import React, { lazy, Suspense, useEffect } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Page, Card, SkeletonBodyText } from '@shopify/polaris';
import { getAffToken, getAdminToken } from './utils/auth';
import * as authDucks from './ducks/auth';

const Register = lazy(() => import('./containers/Auth/Register'));
const VerifyEmail = lazy(() => import('./containers/Auth/VerifyEmail'));
const Login = lazy(() => import('./containers/Auth/Login'));
const ForgotPassword = lazy(() => import('./containers/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./containers/Auth/ResetPassword'));
const Terms = lazy(() => import('./containers/Auth/Terms'));
const Dashboard = lazy(() => import('./containers/Affiliate/Dashboard'));
const Referrals = lazy(() => import('./containers/Affiliate/Referrals'));
const Commissions = lazy(() => import('./containers/Affiliate/Commissions'));
const Payouts = lazy(() => import('./containers/Affiliate/Payouts'));
const Settings = lazy(() => import('./containers/Affiliate/Settings'));
const BrandKit = lazy(() => import('./containers/Affiliate/BrandKit'));
const BannerSheet = lazy(() => import('./containers/Affiliate/BannerSheet'));
const AdminLogin = lazy(() => import('./containers/Admin/AdminLogin'));
const AdminAffiliates = lazy(() => import('./containers/Admin/Affiliates'));
const AdminAffiliateDetail = lazy(() => import('./containers/Admin/AffiliateDetail'));
const AdminWithdrawals = lazy(() => import('./containers/Admin/Withdrawals'));
const AdminCommissions = lazy(() => import('./containers/Admin/Commissions'));

const Fallback = () => (
  <Page>
    <Card><SkeletonBodyText lines={6} /></Card>
  </Page>
);

//affiliate pages: need an affiliate token, load the profile once (used by the top bar)
const RequireAff = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const token = getAffToken();
  const profile = useSelector((s) => s.auth.profile);
  useEffect(() => {
    if (token && !profile) dispatch(authDucks.getProfile());
  }, [dispatch, token, profile]);
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
};

const RequireAdmin = ({ children }) => {
  if (!getAdminToken()) return <Navigate to="/admin/login" replace />;
  return children;
};

const Home = () => <Navigate to={getAffToken() ? '/dashboard' : '/login'} replace />;

const Routes = () => (
  <Suspense fallback={<Fallback />}>
    <RouterRoutes>
      <Route path="/" element={<Home />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/banner-sheet" element={<BannerSheet />} />

      <Route path="/dashboard" element={<RequireAff><Dashboard /></RequireAff>} />
      <Route path="/referrals" element={<RequireAff><Referrals /></RequireAff>} />
      <Route path="/commissions" element={<RequireAff><Commissions /></RequireAff>} />
      <Route path="/payouts" element={<RequireAff><Payouts /></RequireAff>} />
      <Route path="/brand-kit" element={<RequireAff><BrandKit /></RequireAff>} />
      <Route path="/settings" element={<RequireAff><Settings /></RequireAff>} />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/admin/affiliates" replace />} />
      <Route path="/admin/affiliates" element={<RequireAdmin><AdminAffiliates /></RequireAdmin>} />
      <Route path="/admin/affiliates/:id" element={<RequireAdmin><AdminAffiliateDetail /></RequireAdmin>} />
      <Route path="/admin/withdrawals" element={<RequireAdmin><AdminWithdrawals /></RequireAdmin>} />
      <Route path="/admin/commissions" element={<RequireAdmin><AdminCommissions /></RequireAdmin>} />

      <Route path="*" element={<Home />} />
    </RouterRoutes>
  </Suspense>
);

export default Routes;
